import type { ListParams } from './types';

// 与 core-api 共享同一会话存储键，便于管理后台与控制台两个入口共享登录态。
const TOKEN_KEY = 'integral_token';

export class ApiError extends Error {
  status: number;
  /** 后端 4xx 附带的结构化详情（如删除拦截的 details.blockers 明细单），供前台逐条展示。 */
  details?: unknown;
  /**
   * 后端附带的可编程错误码（如 FINANCE_READ_DISABLED / ATTACHMENT_FILE_MISSING）。
   * 有了它，前端才能区分「本环境未启用该能力」与「真的出错了」——
   * 只看 HTTP 状态码时这两者都是 5xx/4xx，会被统一显示成故障。
   */
  code?: string;
  constructor(message: string, status: number, details?: unknown, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.code = code;
  }
}

export function getToken(): string {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
export function setToken(token: string): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable */ }
}

// 会话失效时的回调（由 AuthProvider 注册，用于跳登录页）。
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

export interface DataEnvelope<T> { data: T; }

function buildQuery(params?: ListParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...((init.headers as Record<string, string>) || {}),
  };
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers });
  } catch {
    // P0-1：不再硬编码端口。实际目标由 vite 代理的 VITE_API_TARGET 决定，
    // 这里给出可自证的排查路径：先 /health 确认代理通不通，再看返回的 env_name / core.base_url。
    throw new ApiError(
      '无法连接核心服务。请确认 core-api 已启动，并访问 /health 核对环境名与实际目标地址（勿凭端口猜测）。',
      0,
    );
  }
  if (res.status === 401) {
    if (unauthorizedHandler) unauthorizedHandler();
  }
  const ct = res.headers.get('content-type') || '';
  const body: unknown = ct.includes('json') ? await res.json().catch(() => null) : await res.text().catch(() => '');
  if (!res.ok) {
    const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const msg = obj && 'error' in obj ? String(obj.error) : `请求失败 ${res.status}`;
    throw new ApiError(msg, res.status, obj?.details, obj?.code ? String(obj.code) : undefined);
  }
  return body as T;
}

// 通用：解 {data} 信封。
function unwrap<T>(body: DataEnvelope<T> | T): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as DataEnvelope<T>).data;
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  // 列表与详情统一走 {data} 信封
  list: async <T>(path: string, params?: ListParams): Promise<T[]> =>
    unwrap<T[]>(await request<DataEnvelope<T[]>>(`${path}${buildQuery(params)}`)),
  detail: async <T>(path: string): Promise<T> =>
    unwrap<T>(await request<DataEnvelope<T>>(path)),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(data ?? {}) }),
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(data ?? {}) }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data ?? {}) }),
  del: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
};
